import * as React from "react";
import { base64toHEX, padLeft, secondsToTimeString } from "../../helpers/convert-utils";

import { FormattedMessage, FormattedTime } from "react-intl";
import { ReportDto } from "src/services";

/* eslint-disable import/no-webpack-loader-syntax */
import FailedIcon from "-!svg-react-loader!../../images/icn-failed.svg";
import SuccessIcon from "-!svg-react-loader!../../images/icn-success.svg";
import PsgIcon from "-!svg-react-loader!../../images/psg-logo-invert.svg";
/* eslint-enable import/no-webpack-loader-syntax */
import "./style.css";

interface IReportPreviewProps {
  report: ReportDto;
  isSmallWindow?: boolean;
}

export const ReportPreviewError = (
  <div className="report_error">
    <FailedIcon width="48" height="48" />
    <p className="error-header">
      <FormattedMessage id="report-preview.error.title" defaultMessage="Something went wrong..." />
    </p>
    <p>
      <FormattedMessage
        id="report-preview.error.details"
        defaultMessage="Could not generate report. Wrong report data format."
      />
    </p>
  </div>
);

class ReportPreview extends React.Component<IReportPreviewProps, {}> {
  constructor(props: IReportPreviewProps) {
    super(props);
  }

  public render() {
    let reportData;

    try {
      reportData = JSON.parse(this.props.report.reportData);
    } catch(error) {
      reportData = ''
    }

    if (!reportData) { 
      return null
    };

    return (
      <React.Fragment>
        <div
          className="report_container"
          style={{ height: "98%", overflowY: this.props.isSmallWindow ? "hidden" : "scroll" }}
        >
          <div className="container-fluid">
            <div className="title">
              <span>
                <FormattedTime value={this.props.report.creationTime} year="numeric" month="numeric" day="numeric" />
              </span>
              <br />
              <FormattedMessage id="report-preview.header.title" defaultMessage="Data sanitization report" />
            </div>
            {!this.props.isSmallWindow && (
              <div className="psg-logo">
                <PsgIcon height="43" width="152" />
              </div>
            )}
          </div>
          {this.getPreviewTable(reportData)}
        </div>
      </React.Fragment>
    );
  }

  private getPreviewTable(reportData: any) {
    return (
      <table>
        <tbody>
          {this.getCustomerInfo(reportData)}
          {this.getReportInfo(reportData)}
          {this.getWipeInfo(reportData)}
          {this.getAlgorithmInfo(reportData)}
          {this.getBadBlocksInfo(reportData)}
          {this.getDumpInfo(reportData)}
        </tbody>
      </table>
    );
  }

  private getCustomerInfo(reportData: any) {
    return (
      <React.Fragment>
        <tr>
          <td colSpan={2}>
            <div className="group_header" style={{ paddingTop: this.props.isSmallWindow ? 0 : "20px" }}>
              Customer information
            </div>
          </td>
        </tr>
        <tr>
          <td className="field_key">Person:</td>
          <td className="field_value">{reportData.CustomerName}</td>
        </tr>
        <tr>
          <td className="field_key">Company:</td>
          <td className="field_value">{reportData.CompanyName}</td>
        </tr>
        <tr>
          <td className="field_key">Company address:</td>
          <td className="field_value">{reportData.CompanyAddress}</td>
        </tr>
      </React.Fragment>
    );
  }

  private getReportInfo = (reportData: any) => {
    return (
      <React.Fragment>
        <tr>
          <td colSpan={2}>
            <div className="group_header">Report information</div>
          </td>
        </tr>
        <tr>
          <td className="field_key">Status:</td>
          <td className="field_value">{this.getStatus(reportData.Status)}</td>
        </tr>
        <tr>
          <td className="field_key">Report date:</td>
          <td className="field_value">{reportData.WipeDate}</td>
        </tr>
        <tr>
          <td className="field_key">Tool name:</td>
          <td className="field_value">{reportData.ToolName}</td>
        </tr>
        <tr>
          <td className="field_key">Service tag:</td>
          <td className="field_value">{reportData.ServiceTag}</td>
        </tr>
        <tr>
          <td className="field_key">Report ID:</td>
          <td className="field_value">{reportData.ReportID}</td>
        </tr>
      </React.Fragment>
    );
  };

  //   private getSystemInfo = () => {
  //     return (
  //       <React.Fragment>
  //         <tr>
  //           <td colSpan={2}>
  //             <div className="group_header">System information</div>
  //           </td>
  //         </tr>
  //         <tr>
  //           <td className="field_key">Computer name:</td>
  //           <td className="field_value">{reportData.SysInfoComputerName}</td>
  //         </tr>
  //         <tr>
  //           <td className="field_key">OS version:</td>
  //           <td className="field_value">{reportData.SysInfoOsVersion}</td>
  //         </tr>
  //       </React.Fragment>
  //     );
  //   };

  private getWipeInfo = (reportData: any) => {
    const objectSizeGB = Math.round((100 * reportData.ObjectSize) / 1024 / 1024 / 1024) / 100;

    const duration = secondsToTimeString(reportData.Duration);

    return (
      <React.Fragment>
        <tr>
          <td colSpan={2}>
            <div className="group_header">Wipe information</div>
          </td>
        </tr>
        <tr>
          <td className="field_key">Wipe free space only:</td>
          <td className="field_value">{reportData.FreeSpaceOnly}</td>
        </tr>
        <tr>
          <td className="field_key">Wipe object:</td>
          <td className="field_value">{reportData.WipeObject}</td>
        </tr>
        <tr>
          <td className="field_key">Serial number:</td>
          <td className="field_value">{reportData.SerialNumber}</td>
        </tr>
        <tr>
          <td className="field_key">Model number:</td>
          <td className="field_value">{reportData.ModelNumber}</td>
        </tr>
        <tr>
          <td className="field_key">Disk ID:</td>
          <td className="field_value">{reportData.DiskID}</td>
        </tr>
        <tr>
          <td className="field_key">Object size:</td>
          <td className="field_value">
            {objectSizeGB} GB ({reportData.ObjectSize} bytes)
          </td>
        </tr>
        <tr>
          <td className="field_key">Start time:</td>
          <td className="field_value">{reportData.StartTime}</td>
        </tr>
        <tr>
          <td className="field_key">Duration:</td>
          <td className="field_value">{duration}</td>
        </tr>
        <tr>
          <td className="field_key">Wipe stamp:</td>
          <td className="field_value">{reportData.SetWipeStamp}</td>
        </tr>
      </React.Fragment>
    );
  };

  private getAlgorithmInfo = (reportData: any) => {
    return (
      <React.Fragment>
        <tr>
          <td colSpan={2}>
            <div className="group_header">Algorithm information</div>
          </td>
        </tr>
        <tr>
          <td className="field_key">Wipe method:</td>
          <td className="field_value">{reportData.WipeMethod}</td>
        </tr>
        <tr>
          <td className="field_key">Passes count:</td>
          <td className="field_value">{reportData.PassCount}</td>
        </tr>
        <tr>
          <td className="field_key">Verification passes count:</td>
          <td className="field_value">{reportData.VerifyPassesCount}</td>
        </tr>
        <tr>
          <td className="field_key">Percentage of sectors checked:</td>
          <td className="field_value">{reportData.PercentageOfSectorsToCheck}%</td>
        </tr>
      </React.Fragment>
    );
  };

  private getBadBlocksInfo = (reportData: any) => {
    return (
      <React.Fragment>
        <tr>
          <td colSpan={2}>
            <div className="group_header">Bad block information</div>
          </td>
        </tr>
        <tr>
          <td className="field_key">Total bad blocks:</td>
          <td className="field_value">{reportData.TotalBadBlocks || "no bad blocks found"}</td>
        </tr>
      </React.Fragment>
    );
  };

  private getDumpInfo = (reportData: any) => {
    const first = "0x" + padLeft(20, reportData.FirstSector.toString(16));
    const middle = "0x" + padLeft(20, reportData.MiddleSector.toString(16));
    const last = "0x" + padLeft(20, reportData.EndSector.toString(16));

    return (
      <React.Fragment>
        <tr>
          <td colSpan={2}>
            <div className="group_header">Dump</div>
          </td>
        </tr>
        <tr>
          <td className="field_key">First sector:</td>
          <td className="field_value dump">{first}</td>
        </tr>
        <tr>
          <td className="field_key">First sector dump:</td>
          <td className="field_value">{this.getDump(reportData.FirstSectorDump)}</td>
        </tr>
        <tr>
          <td className="field_key">Middle sector:</td>
          <td className="field_value dump">{middle}</td>
        </tr>
        <tr>
          <td className="field_key">Middle sector dump:</td>
          <td className="field_value">{this.getDump(reportData.MiddleSectorDump)}</td>
        </tr>
        <tr>
          <td className="field_key">End sector:</td>
          <td className="field_value dump">{last}</td>
        </tr>
        <tr>
          <td className="field_key">End sector dump:</td>
          <td className="field_value">{this.getDump(reportData.EndSectorDump)}</td>
        </tr>
      </React.Fragment>
    );
  };

  private getDump(base64: string) {
    const hex = base64toHEX(base64);

    let start = 0;
    let offset = 0;
    const len = hex.length;

    const lines = [];
    while (start < len) {
      lines.push(`0x${padLeft(8, offset.toString(16))}: ${hex.slice(start, start + 2 * 16 + 16)}`);
      start += 2 * 16 + 16;

      offset += 16;
    }

    return (
      <div className="dump">
        {lines.map(l => (
          <div key={l}>{l}</div>
        ))}
      </div>
    );
  }

  private getStatus(status: string) {
    if (status === "Successful") {
      return (
        <div className="report_status success">
          <SuccessIcon height="16" style={{ paddingRight: "5px", color: "#02D2DC" }} /> {status}
        </div>
      );
    } else {
      return (
        <div className="report_status failed">
          <FailedIcon height="16" style={{ paddingRight: "5px", color: "#FF2B59" }} /> {status}
        </div>
      );
    }
  }
}

export default ReportPreview;



// WEBPACK FOOTER //
// ./src/components/report-preview/index.tsx